/**
 * TanStack Router — finance-only.
 *
 * Изолированный routeTree для finance.<домен>: только финансовые страницы +
 * auth (login/reset/profile). Роуты на КОРНЕ (subdomain isolation) — без
 * префикса /finance. Tree-shaking выкидывает дашборд/мессенджер/склад.
 *
 * Гарда — protectedRoute с AuthGate. После логина — Navigate "/" → FinancePage.
 */
import {
  Outlet,
  Navigate,
  createRootRouteWithContext,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";

import { FinanceShell } from "@/components/FinanceShell";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import { LoginPage } from "@/pages/LoginPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { FinancePage } from "@/pages/FinancePage";
import { FinanceCashflowPage } from "@/pages/FinanceCashflowPage";
import { FinanceObjectsPage } from "@/pages/FinanceObjectsPage";
import { FinanceArAgingPage } from "@/pages/FinanceArAgingPage";
import { FinanceTaxesPage } from "@/pages/FinanceTaxesPage";
import { FinanceForecastPage } from "@/pages/FinanceForecastPage";
import { FinanceWipPage } from "@/pages/FinanceWipPage";
import { FinanceFundsPage } from "@/pages/FinanceFundsPage";
import { FinanceExpensesPage } from "@/pages/FinanceExpensesPage";
import { FinanceCategoriesPage } from "@/pages/FinanceCategoriesPage";
import { FinanceStaffPage } from "@/pages/FinanceStaffPage";
import { FinancePayrollPage } from "@/pages/FinancePayrollPage";
import { FinanceDividendPayoutsPage } from "@/pages/FinanceDividendPayoutsPage";
import { FinanceAccrualsPage } from "@/pages/FinanceAccrualsPage";
import { FinanceMotivationRulesPage } from "@/pages/FinanceMotivationRulesPage";
import { FinanceAccessPage } from "@/pages/FinanceAccessPage";
import { FinanceSummaryPage } from "@/pages/FinanceSummaryPage";
import { ProductionPage } from "@/pages/ProductionPage";
import { ProductionDetailPage } from "@/pages/ProductionDetailPage";


interface RouterContext {
  queryClient: QueryClient;
}


function AuthGate() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <div className="muted">Проверка сессии…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Финансы — по явному per-user-флагу can_access_finance, либо
  // role в {owner, admin, accountant, superuser}.
  if (!hasAccess(user, "finance")) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Доступ ограничен</div>
          <div style={{ color: "var(--mute)", marginBottom: 16 }}>
            Раздел Финансы доступен только по индивидуальному разрешению.
            Обратитесь к собственнику.
          </div>
          <a href="/messenger" className="btn btn-primary">Перейти в Мессенджер</a>
        </div>
      </div>
    );
  }
  return <FinanceShell />;
}


function GuestGate() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}


const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: GuestGate,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: AuthGate,
});

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: FinancePage,
});

const cashflowRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/cashflow",
  component: FinanceCashflowPage,
});

const objectsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/objects",
  component: FinanceObjectsPage,
});

const arAgingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/ar-aging",
  component: FinanceArAgingPage,
});

const taxesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/taxes",
  component: FinanceTaxesPage,
});

const forecastRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/forecast",
  component: FinanceForecastPage,
});

const wipRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/wip",
  component: FinanceWipPage,
});

const fundsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/funds",
  component: FinanceFundsPage,
});

const expensesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/expenses",
  component: FinanceExpensesPage,
});

const categoriesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/categories",
  component: FinanceCategoriesPage,
});

const staffRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/staff",
  component: FinanceStaffPage,
});

const payrollRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/payroll",
  component: FinancePayrollPage,
});

// /dividends объединён с /payouts: на одной странице и текущий расчёт-превью,
// и журнал выплат, и фиксация. Старый путь сохранён как алиас.
const dividendsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/dividends",
  component: FinanceDividendPayoutsPage,
});

const payoutsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/payouts",
  component: FinanceDividendPayoutsPage,
});

const accrualsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/accruals",
  component: FinanceAccrualsPage,
});

const motivationRulesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/motivation-rules",
  component: FinanceMotivationRulesPage,
});

const accessRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/access",
  component: FinanceAccessPage,
});

const summaryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/summary",
  component: FinanceSummaryPage,
});

const productionRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/production",
  component: ProductionPage,
});

const productionDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/production/$projectId",
  component: ProductionDetailPage,
});

const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile",
  component: ProfilePage,
});

const profileViewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile/$userId",
  component: ProfilePage,
});


const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  protectedRoute.addChildren([
    indexRoute,
    cashflowRoute,
    objectsRoute,
    arAgingRoute,
    taxesRoute,
    forecastRoute,
    wipRoute,
    fundsRoute,
    expensesRoute,
    categoriesRoute,
    staffRoute,
    payrollRoute,
    dividendsRoute,
    payoutsRoute,
    accrualsRoute,
    motivationRulesRoute,
    accessRoute,
    summaryRoute,
    productionRoute,
    productionDetailRoute,
    profileRoute,
    profileViewRoute,
  ]),
]);


export function buildFinanceRouter(queryClient: QueryClient) {
  const rawBase = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? "/";
  const basepath = rawBase === "/" ? undefined : rawBase.replace(/\/$/, "");
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    basepath,
  });
}

// NB: declare module Register живёт в router.tsx (общий проект) — здесь не
// дублируем, иначе TS-конфликт двух routeTree. Runtime не страдает.
